import { TagIcon as TagIconOutline } from "@heroicons/react/outline";
import { TagIcon } from "@heroicons/react/solid";
import { Check, Plus, Search } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import { Button } from "./ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

import { useAuth } from "@/context/auth-context";
import { useRbac } from "@/providers/RbacProvider";
import { EntityType } from "@/queries/folder-entities/types";
import { useWorkspaceTags } from "@/queries";

interface TagsEditorProps {
  tags: string[];
  onEdit: (newTags: string[]) => void;
  maxTags?: number;
  type?: EntityType;
}

// Fixed height for consistent list area; 9 rows × 2rem (~32px) = 288px
const TAG_LIST_HEIGHT = 280;
const VISIBLE_BATCH_SIZE = 100;

const TagsEditor: React.FC<TagsEditorProps> = ({ tags, onEdit, maxTags = 10, type }) => {
  const [isEditing, setIsEditing] = useState(false);
  const authContext = useAuth();
  const { workspaceId } = useParams();
  const [inputValue, setInputValue] = useState("");
  const [visibleCount, setVisibleCount] = useState(VISIBLE_BATCH_SIZE);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const { isEditDisabled } = useRbac();
  const cannotEdit = isEditDisabled(type as EntityType);

  const { data: internalTags, refetch: refetchTags } = useWorkspaceTags(
    authContext?.userToken ?? "",
    {
      workspaceId: Number(workspaceId!),
    },
  );

  useEffect(() => {
    if (isEditing) {
      refetchTags();
    }
  }, [isEditing, refetchTags]);

  useEffect(() => {
    if (isEditing) {
      // Small delay to ensure popover is rendered before focusing
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    }

    setInputValue("");
    setVisibleCount(VISIBLE_BATCH_SIZE);
  }, [isEditing]);

  const handleAddTag = useCallback(
    (value?: string) => {
      const tagValue = (value ?? inputValue).trim();
      if (tagValue && tags.length < maxTags && !tags.includes(tagValue)) {
        const newTags = [...tags, tagValue];
        onEdit(newTags);
        setInputValue("");
      }
    },
    [inputValue, tags, maxTags, onEdit],
  );

  const handleRemoveTag = useCallback(
    (tagToRemove: string) => {
      const newTags = tags.filter(tag => tag !== tagToRemove);
      onEdit(newTags);
    },
    [tags, onEdit],
  );

  const handleToggleTag = useCallback(
    (tag: string) => {
      if (cannotEdit) {
        return;
      }
      if (tags.includes(tag)) {
        handleRemoveTag(tag);
      } else if (tags.length < maxTags) {
        onEdit([...tags, tag]);
      }
    },
    [tags, cannotEdit, maxTags, onEdit, handleRemoveTag],
  );

  const trimmedQuery = inputValue.trim().toLowerCase();

  const filteredTags = useMemo(() => {
    const suggestedTags = Array.isArray(internalTags) ? internalTags : [];

    // Build a deduplicated combined list: current tags + suggested tags
    const combined = new Set<string>(tags);
    for (const tag of suggestedTags) {
      combined.add(tag);
    }

    let result = Array.from(combined);

    // Filter by query
    if (trimmedQuery) {
      result = result.filter(tag => tag.toLowerCase().includes(trimmedQuery));
    }

    // Sort: selected tags first, then alphabetical. When searching, prioritize startsWith matches.
    result.sort((a, b) => {
      const aSelected = tags.includes(a) ? 0 : 1;
      const bSelected = tags.includes(b) ? 0 : 1;

      // Selected tags first
      if (aSelected !== bSelected) {
        return aSelected - bSelected;
      }

      if (trimmedQuery) {
        const aLower = a.toLowerCase();
        const bLower = b.toLowerCase();
        const aStarts = aLower.startsWith(trimmedQuery) ? 0 : 1;
        const bStarts = bLower.startsWith(trimmedQuery) ? 0 : 1;
        if (aStarts !== bStarts) {
          return aStarts - bStarts;
        }
      }

      return a.toLowerCase().localeCompare(b.toLowerCase());
    });

    return result;
  }, [internalTags, tags, trimmedQuery]);

  const visibleTags = useMemo(
    () => filteredTags.slice(0, visibleCount),
    [filteredTags, visibleCount],
  );

  const hasMore = filteredTags.length > visibleCount;

  // Check if the current query exactly matches an existing tag (case-insensitive)
  const exactMatchExists = useMemo(() => {
    if (!trimmedQuery) {
      return false;
    }
    return filteredTags.some(tag => tag.toLowerCase() === trimmedQuery);
  }, [filteredTags, trimmedQuery]);

  const showCreateOption =
    !cannotEdit && trimmedQuery && !exactMatchExists && tags.length < maxTags;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (showCreateOption) {
        handleAddTag();
      } else if (trimmedQuery && exactMatchExists) {
        // Toggle the exact match
        const exactTag = filteredTags.find(tag => tag.toLowerCase() === trimmedQuery);
        if (exactTag) {
          handleToggleTag(exactTag);
          setInputValue("");
        }
      }
    } else if (e.key === "Backspace" && inputValue === "" && tags.length > 0) {
      handleRemoveTag(tags[tags.length - 1]);
    } else if (e.key === "Escape") {
      setIsEditing(false);
    }
  };

  const handleScroll = useCallback(() => {
    const el = listRef.current;
    if (!el || !hasMore) {
      return;
    }
    const { scrollTop, scrollHeight, clientHeight } = el;
    if (scrollHeight - scrollTop - clientHeight < 80) {
      setVisibleCount(prev => prev + VISIBLE_BATCH_SIZE);
    }
  }, [hasMore]);

  // Reset visible count when query changes
  useEffect(() => {
    setVisibleCount(VISIBLE_BATCH_SIZE);
  }, [trimmedQuery]);

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Popover open={isEditing} onOpenChange={setIsEditing}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsEditing(true)}
            className={`gap-x-1 rounded-full bg-gray-50 px-2 py-2.5 text-xs ${
              tags.length > 0
                ? "text-blue-600 hover:text-blue-700"
                : "text-gray-500  hover:text-gray-700"
            }`}
          >
            {tags.length > 0 ? (
              <TagIcon className="h-2.5 w-2.5 flex-shrink-0" />
            ) : (
              <TagIconOutline className="h-2.5 w-2.5 flex-shrink-0" />
            )}
            {tags.length === 0 ? "No Tags" : "Tags"}
            {tags.length > 0 && (
              <span className="ml-1 inline-flex items-center justify-center rounded-full bg-blue-100 px-1 py-0.5 text-xs font-bold leading-none text-blue-600">
                {tags.length}
              </span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="max-h-[min(70vh,500px)] w-72 overflow-hidden p-0"
          align="start"
          onOpenAutoFocus={e => {
            e.preventDefault();
            inputRef.current?.focus();
          }}
        >
          <div className="flex min-h-0 flex-col">
            {/* Search input */}
            {!cannotEdit && (
              <div className="flex items-center gap-2 border-b border-gray-100 px-3 py-2">
                <Search className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
                <input
                  ref={inputRef}
                  type="text"
                  value={inputValue}
                  onChange={e => setInputValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Search or create tag..."
                  className="h-7 w-full border-0 bg-transparent text-sm outline-none placeholder:text-gray-400 focus:ring-0"
                />
              </div>
            )}

            {/* Tag list */}
            <div
              ref={listRef}
              className="min-h-0 shrink-0 overflow-y-auto overscroll-contain"
              style={{
                maxHeight: `${TAG_LIST_HEIGHT}px`,
                scrollbarGutter: "stable",
              }}
              onScroll={handleScroll}
            >
              {/* Create new tag option */}
              {showCreateOption && (
                <button
                  type="button"
                  className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm text-blue-600 transition-colors hover:bg-blue-50"
                  onClick={() => handleAddTag()}
                >
                  <Plus className="h-3.5 w-3.5 flex-shrink-0" />
                  <span className="truncate">
                    Create <span className="font-medium">"{inputValue.trim()}"</span>
                  </span>
                </button>
              )}

              {/* Tags */}
              {visibleTags.length > 0 ? (
                <div className="py-1">
                  {visibleTags.map(tag => {
                    const isSelected = tags.includes(tag);
                    return (
                      <button
                        type="button"
                        key={tag}
                        className={`flex w-full items-center gap-2.5 px-3 py-1.5 text-left text-sm transition-colors ${
                          cannotEdit
                            ? "cursor-default"
                            : "cursor-pointer hover:bg-gray-50 active:bg-gray-100"
                        }`}
                        onClick={() => handleToggleTag(tag)}
                        disabled={cannotEdit}
                      >
                        <span
                          className={`flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border transition-colors ${
                            isSelected
                              ? "border-blue-500 bg-blue-500 text-white"
                              : "border-gray-300 bg-white"
                          }`}
                        >
                          {isSelected && <Check className="h-3 w-3" strokeWidth={2.5} />}
                        </span>
                        <span className="truncate">{tag}</span>
                      </button>
                    );
                  })}

                  {hasMore && (
                    <div className="px-3 py-2 text-center text-xs text-gray-400">
                      Showing {visibleCount} of {filteredTags.length} tags — scroll for more
                    </div>
                  )}
                </div>
              ) : (
                !showCreateOption && (
                  <div className="px-3 py-6 text-center text-sm text-gray-400">
                    {trimmedQuery ? (
                      <>
                        No tags matching "<span className="font-medium">{inputValue.trim()}</span>"
                      </>
                    ) : (
                      "No tags yet"
                    )}
                  </div>
                )
              )}
            </div>

            {/* Footer with count */}
            {tags.length > 0 && (
              <div className="border-t border-gray-100 px-3 py-1.5 text-xs text-gray-400">
                {tags.length} of {maxTags} tags selected
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default TagsEditor;
